import type { UploadedFile } from '../types'
import { FileDropzone } from './file-dropzone'

type DocumentUploadSectionProps = {
  uploadedFiles: UploadedFile[]
  uploadLoading: boolean
  onFilesSelected: (files: File[]) => void
  onDeleteFile: (filename: string) => void
}

function formatFileSize(bytes: number | null): string {
  if (bytes === null) return 'Unknown'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function DocumentUploadSection({
  uploadedFiles,
  uploadLoading,
  onFilesSelected,
  onDeleteFile,
}: DocumentUploadSectionProps) {
  return (
    <div className="card p-6 mb-6">
      <h2 className="text-lg font-semibold text-theme-primary mb-4">Document Upload</h2>
      <p className="text-sm text-theme-secondary mb-4">
        Upload private documents to the internal knowledge base.
        Use <code className="bg-surface-elevated px-1 rounded text-xs">[INTERNAL]</code> in chat to search these documents.
      </p>

      <FileDropzone
        onFilesSelected={onFilesSelected}
        disabled={uploadLoading}
        isUploading={uploadLoading}
      />

      <h3 className="text-base font-medium text-theme-primary mt-6 mb-3">Uploaded Files</h3>
      <div className="space-y-2 max-h-[280px] overflow-y-auto">
        {uploadedFiles.map((file) => (
          <div
            key={file.source_filename}
            className="flex items-center justify-between p-3 bg-surface-elevated rounded-md"
          >
            <div className="min-w-0 mr-2">
              <span className="text-sm font-medium text-theme-primary truncate block">
                {file.source_filename}
              </span>
              <span className="text-xs text-theme-muted">
                {file.source_type.toUpperCase()}, {formatFileSize(file.file_size_bytes)}, {file.total_chunks} chunks
              </span>
            </div>
            <button
              onClick={() => onDeleteFile(file.source_filename)}
              className="btn-danger btn-sm shrink-0"
            >
              Delete
            </button>
          </div>
        ))}
        {uploadedFiles.length === 0 && (
          <p className="text-theme-muted text-sm">No documents uploaded yet</p>
        )}
      </div>
    </div>
  )
}
